import { Alert, Button, Flex, Input, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { dismissReminder, sendReminder, type Reminder } from './api';

const { Text } = Typography;

export interface ReminderDrawerProps {
  open: boolean;
  reminder: Reminder;
  onClose: () => void;
  onDone: () => void;
}

/** What sending needs, so a reminder call on Follow-ups can send without loading the reminder. */
export interface SendableReminder {
  uid: string;
  customer: { name: string; phone: string };
  unit: { plateNo: string | null; chassisNo: string };
}

const bikeOf = (r: SendableReminder): string => r.unit.plateNo ?? r.unit.chassisNo;

/**
 * NKS has no WhatsApp Business API (CLAUDE.md §13e): the API marks the reminder
 * sent and returns a wa.me link, and staff press Send in their own WhatsApp.
 *
 * Two steps on purpose. The link is shown as a real anchor AFTER the send is
 * recorded, never opened by script: a window.open after an await is a blocked
 * popup on iOS and in an installed PWA. Refusals (no consent, no phone) are the
 * server's words, shown as written.
 */
export function SendReminderDrawer({
  open,
  reminder,
  onClose,
  onDone,
}: Omit<ReminderDrawerProps, 'reminder'> & { reminder: SendableReminder }): ReactNode {
  const { t } = useTranslation();
  const [waLink, setWaLink] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const send = async (): Promise<void> => {
    setSubmitting(true);
    setFailure(null);
    try {
      const result = await sendReminder(reminder.uid);
      setWaLink(result.waLink);
      setSent(true);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : t('serviceDesk.reminders.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const finish = (): void => {
    setFailure(null);
    if (sent) onDone();
    else onClose();
  };

  return (
    <ActionDrawer
      open={open}
      title={t('serviceDesk.reminders.sendTitle')}
      submitLabel={t('serviceDesk.reminders.sendSubmit')}
      submitting={submitting}
      consequence={
        sent
          ? undefined
          : t('serviceDesk.reminders.sendConsequence', {
              name: reminder.customer.name,
              bike: bikeOf(reminder),
              phone: reminder.customer.phone,
            })
      }
      onSubmit={() => void send()}
      onClose={finish}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
      footer={
        sent ? (
          <Flex justify="flex-end" gap={8}>
            <Button size="large" onClick={finish}>
              {t('serviceDesk.reminders.done')}
            </Button>
            {waLink !== null && (
              <Button
                size="large"
                type="primary"
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('serviceDesk.reminders.open')}
              </Button>
            )}
          </Flex>
        ) : undefined
      }
    >
      {sent ? (
        <Alert type="success" showIcon message={t('serviceDesk.reminders.sendReady', { phone: reminder.customer.phone })} />
      ) : (
        <Flex vertical gap={4}>
          <Text strong>{reminder.customer.name}</Text>
          <Text type="secondary">{reminder.customer.phone}</Text>
          <Text type="secondary">{bikeOf(reminder)}</Text>
        </Flex>
      )}
    </ActionDrawer>
  );
}

export function DismissReminderDrawer({ open, reminder, onClose, onDone }: ReminderDrawerProps): ReactNode {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const dismiss = async (): Promise<void> => {
    setSubmitting(true);
    setFailure(null);
    try {
      await dismissReminder(reminder.uid, { reason: reason.trim() });
      onDone();
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : t('serviceDesk.reminders.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionDrawer
      open={open}
      title={t('serviceDesk.reminders.dismissTitle')}
      submitLabel={t('serviceDesk.reminders.dismissSubmit')}
      submitting={submitting}
      submitDisabled={reason.trim().length === 0}
      danger
      consequence={t('serviceDesk.reminders.dismissConsequence', {
        name: reminder.customer.name,
        bike: bikeOf(reminder),
      })}
      onSubmit={() => void dismiss()}
      onClose={() => {
        setFailure(null);
        onClose();
      }}
      error={failure !== null ? <Text type="danger">{failure}</Text> : undefined}
    >
      <label>
        <Text>{t('serviceDesk.reminders.dismissReason')}</Text>
        <Input.TextArea
          rows={3}
          maxLength={255}
          autoFocus
          value={reason}
          onChange={(e) => { setReason(e.target.value); }}
        />
      </label>
    </ActionDrawer>
  );
}
